import { getBlogSlug } from '@blog/utils';
import {
    DocumentHit,
    DropboxHit,
    OnlineTrainingHit,
    SearchCategory,
    SearchHit,
    SkuSearchResultsDocumentTypeConfig,
    SkuSearchResultsDocumentTypes,
} from '@common/types/search';
import { BASE_URL } from '@constants/common';
import { store } from '@store/store';

import BookIcon from '@assets/icons/book.svg?react';
import HomeStorageIcon from '@assets/icons/home-storage.svg?react';
import MediaFilesIcon from '@assets/icons/media-files.svg?react';
import { DataThresholdingOutlined } from '@mui/icons-material';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import MemoryIcon from '@mui/icons-material/Memory';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PlayLessonOutlinedIcon from '@mui/icons-material/PlayLessonOutlined';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import { getDocumentDownloadPath, getVictronDocumentUrl, isDocumentUploadUrl } from '@utils/documentDownload';
import { getTranslationFromRegionStrings } from '@utils/translations';
import { createElement } from 'react';
import { defineMessage, FormattedMessage, MessageDescriptor } from 'react-intl';

export const PROFESSIONAL_MEILISEARCH_HOST = import.meta.env.VITE_PROFESSIONAL_MEILISEARCH_HOST;
export const PROFESSIONAL_MEILISEARCH_KEY = import.meta.env.VITE_PROFESSIONAL_MEILISEARCH_SEARCH_KEY;
export const CMS_DOCUMENTS_MEILISEARCH_HOST = import.meta.env.VITE_CMS_DOCUMENTS_MEILISEARCH_HOST;
export const CMS_DOCUMENTS_MEILISEARCH_KEY = import.meta.env.VITE_CMS_DOCUMENTS_MEILISEARCH_SEARCH_KEY;

export const isSearchHit = (item: SearchHit | DocumentHit): item is SearchHit => {
    return !('document_name' in item);
};

export const isDropboxHit = (item: SearchHit | DropboxHit): item is DropboxHit => {
    return 'download_path' in item;
};

export const SkuSearchResultsDocumentTypeConfigs: SkuSearchResultsDocumentTypeConfig[] = [
    {
        type: SkuSearchResultsDocumentTypes.DATASHEET,
        icon: DataThresholdingOutlined,
        name: defineMessage({ id: 'search.skuResults.datasheets', defaultMessage: 'Datasheets' }),
    },
    {
        type: SkuSearchResultsDocumentTypes.PRODUCT_MANUAL,
        icon: BookIcon,
        name: defineMessage({ id: 'search.skuResults.productManuals', defaultMessage: 'Product Manuals' }),
    },
];

export const EXTERNAL_URL_REGEX = /^https?:\/\//i;

export const eventSearchCategory: SearchCategory<SearchHit> = {
    name: defineMessage({ id: 'search.category.events', defaultMessage: 'Events' }),
    icon: DateRangeOutlinedIcon,
    index: 'events',
    in: true,
    url: {
        getHref: (event: SearchHit) => event.url ?? '',
    },
    weight: 1,
};

export const newsSearchCategory: SearchCategory<SearchHit> = {
    name: defineMessage({ id: 'search.category.newsArticles', defaultMessage: 'News Articles' }),
    icon: NewspaperIcon,
    index: 'news',
    // MAKE SURE MEILISEARCH RETURNS REACT URLS (/app/news/<id>) FOR NEWS ARTICLES, OTHERWISE THIS WILL NOT WORK
    in: true,
    url: {
        getHref: (article: SearchHit) => article.url ?? '',
    },
    weight: 2,
};

const getOnlineTrainingTitle = (training: OnlineTrainingHit) => {
    const locale = store.getState().translation.locale;
    const localeTitle = training[`title_${locale}`];

    return localeTitle || training.title || '';
};

export const onlineTrainingSearchCategory: SearchCategory<SearchHit> = {
    name: defineMessage({ id: 'search.category.onlineTraining', defaultMessage: 'Online Training' }),
    icon: PlayLessonOutlinedIcon,
    index: 'online-training',
    in: false,
    url: {
        getHref: (training) => training.url ?? '',
    },
    chip: (training) => {
        const message = OnlineTrainingCategories[training.category ?? ''];
        return message ? createElement(FormattedMessage, message) : null;
    },
    title: (training) => getOnlineTrainingTitle(training),
    weight: 2.5,
};

export const otherProfessionalSearchCategories: SearchCategory<SearchHit>[] = [
    {
        name: defineMessage({ id: 'search.category.firmware', defaultMessage: 'Firmware' }),
        icon: MemoryIcon,
        index: 'firmware',
        in: false,
        url: {
            getHref: (file: SearchHit) => `${BASE_URL}${file.path ?? ''}`,
            target: '_blank',
        },
        weight: 5,
    },
    {
        name: defineMessage({ id: 'search.category.videos', defaultMessage: 'Videos' }),
        icon: SmartDisplayOutlinedIcon,
        index: 'videos',
        in: true,
        url: {
            getHref: (video: SearchHit) =>
                `/app/videos${video.external_id ? `?v=${encodeURIComponent(video.external_id)}` : ''}`,
        },
        weight: 4,
    },
    {
        name: defineMessage({ id: 'search.category.mediaAssets', defaultMessage: 'Media Files' }),
        icon: MediaFilesIcon,
        index: 'media-assets',
        in: true,
        url: {
            getHref: (asset: SearchHit | DropboxHit) => (isDropboxHit(asset) ? asset.download_path : asset.url ?? ''),
            getTarget: (asset: SearchHit | DropboxHit) => (isDropboxHit(asset) ? '_blank' : undefined),
        },
        downloadable: true,
        weight: 7,
    },
    {
        index: 'repair-documents',
        scope: 'repairs',
        name: defineMessage({ id: 'search.category.repairFiles', defaultMessage: 'Repair Files' }),
        icon: HomeStorageIcon,
        in: true,
        url: {
            getHref: (rd: SearchHit | DropboxHit) => (isDropboxHit(rd) ? rd.download_path : rd.url ?? ''),
            getTarget: (rd: SearchHit | DropboxHit) => (isDropboxHit(rd) ? '_blank' : undefined),
        },
        downloadable: true,
        weight: 6,
    },
];

export const getCmsSearchCategories = (locale: string): SearchCategory<SearchHit | DocumentHit>[] => [
    {
        index: 'documents',
        name: defineMessage({ id: 'search.category.productResources', defaultMessage: 'Product Resources' }),
        icon: WorkspacesOutlinedIcon,
        in: false,
        url: {
            getHref: (item: SearchHit | DocumentHit) => {
                if (isSearchHit(item)) return '';

                const documentUrl = getTranslationFromRegionStrings(item.url, locale);
                return isDocumentUploadUrl(documentUrl)
                    ? getDocumentDownloadPath(documentUrl)
                    : getVictronDocumentUrl(documentUrl);
            },
            getTarget: (item: SearchHit | DocumentHit) => {
                if (isSearchHit(item)) return undefined;

                const documentUrl = getTranslationFromRegionStrings(item.url, locale);
                return isDocumentUploadUrl(documentUrl) ? undefined : '_blank';
            },
        },
        downloadable: true,
        weight: 8,
    },
    {
        name: defineMessage({ id: 'search.category.blogPosts', defaultMessage: 'Blog Posts' }),
        icon: FeedOutlinedIcon,
        index: 'blog',
        in: true,
        url: {
            getHref: (post: SearchHit | DocumentHit) =>
                `/app/blog/${getBlogSlug(isSearchHit(post) ? post.link ?? '' : '')}`,
        },
        weight: 3,
    },
];

export const OnlineTrainingCategories: Record<string, MessageDescriptor> = {
    BT: defineMessage({ id: 'search.onlineTraining.category.basic', defaultMessage: 'Basic Training' }),
    TH: defineMessage({ id: 'search.onlineTraining.category.theory', defaultMessage: 'Theory' }),
    AT: defineMessage({ id: 'search.onlineTraining.category.advanced', defaultMessage: 'Advanced Training' }),
    IE: defineMessage({
        id: 'search.onlineTraining.category.installationExamples',
        defaultMessage: 'Installation Examples',
    }),
    TT: defineMessage({ id: 'search.onlineTraining.category.technical', defaultMessage: 'Technical Training' }),
    OT: defineMessage({ id: 'search.onlineTraining.category.other', defaultMessage: 'Other' }),
};

export const SearchScopes: Record<string, MessageDescriptor> = {
    'media-assets': defineMessage({ id: 'search.category.media_assets', defaultMessage: 'Media Assets' }),
    firmware: defineMessage({ id: 'search.category.firmware', defaultMessage: 'Firmware' }),
    repairs: defineMessage({ id: 'search.category.repairs', defaultMessage: 'Repairs Files' }),
    news: defineMessage({ id: 'search.category.news', defaultMessage: 'News' }),
    'online-training': defineMessage({ id: 'search.category.onlineTraining', defaultMessage: 'Online Training' }),
    videos: defineMessage({ id: 'search.category.videos', defaultMessage: 'Videos' }),
    blog: defineMessage({ id: 'search.category.blog', defaultMessage: 'Blog' }),
    events: defineMessage({ id: 'search.category.events', defaultMessage: 'Events' }),
};
